import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { Color, FontSize, CommonProperties, CommonStyles as CS } from '../../styles';
import Images from '../../constants/Images';
import Icon from '../Icon';
import { formatCurrency } from '../../utils/currency.utils';
import { carSearchType } from '../../constants/Enums';
import { useDispatch } from 'react-redux';
import { selectCar } from '../../store/actions/car';
import TextWithIcon from '../TextWithIcon';
import Card from './Card';
import PriceItem from './PriceItem';
import { cardStyle, XSmallText, CardLeftTextDetail, ShrinkView, PerUnitPrice } from './card.utils';
import RowView from '../RowView';
import { useTranslation } from 'react-i18next';
import i18n, { useComponentTKey, i18nNS } from '../../i18n';
import { changeCdnUrl } from '../../utils/common.utils';
import { useDateFormat } from '../../utils/useDateFormat';

const transformCarDataToCard = (item = {}, currency, currencyCulture) => {
  let distance = null;
  if (item.PickupDistanceUnit && !isNaN(item.PickupDistance)) {
    distance =
      (item.PickupDistance !== 0 ? Number(item.PickupDistance).toFixed(2) : 0.01) +
      ' ' +
      item.PickupDistanceUnit;
  }
  return {
    title: item.Class,
    subtitle: item.CarName.toUpperCase().replace(
      'OR SIMILAR',
      i18n.t('resultCards:CarCard.Similar')
    ),
    value: formatCurrency(item.MinimumfarePerDay, currency, currencyCulture),
    textUpValue: distance,
    textDownValue:
      item.RequestType === carSearchType.AIRPORT
        ? i18n.t('resultCards:CarCard.OnAirport')
        : i18n.t('resultCards:CarCard.OffAirport'),
    icon: { uri: item.CarImageURL },
    imageDownIcon: Images.car['50x20'][item.VendorCode],
    policy: item.OutSidePolicyException?.ReasonCode?.toLowerCase(),
  };
};

const AccesoryItem = ({ iconName, number }) => {
  return (
    <View style={[CS.row, CS.ml2]}>
      <Icon style={styles.accesoryItemIcon} name={iconName} />
      <XSmallText style={[styles.grayColor, CS.ml2]}>{number}</XSmallText>
    </View>
  );
};

const CarDetails = ({ item = {}, showBrands, showPerUnitPrice, currency, currencyCulture }) => {
  const { tKey } = useComponentTKey('CarCard');
  const { t } = useTranslation(i18nNS.resultCards);
  const dispatch = useDispatch();
  const formatDate = useDateFormat();

  return (
    <View style={cardStyle.composedRow}>
      <ShrinkView>
        <RowView>
          <CardLeftTextDetail />
          <ShrinkView>
            <XSmallText>
              {item.VendorName} {t(tKey('RentalCar'))}
            </XSmallText>
          </ShrinkView>
        </RowView>
        <RowView>
          <CardLeftTextDetail>{t(tKey('Pickup'))}</CardLeftTextDetail>
          <ShrinkView>
            <XSmallText>{item.PickupTime + ' ' + formatDate(item.PickupDate)}</XSmallText>
            <XSmallText style={styles.grayColor}>
              {item.PickupAddress
                ? item.PickupCityState + '/' + item.PickupAddress
                : item.PickupAirport}
            </XSmallText>
          </ShrinkView>
        </RowView>
        <RowView>
          <CardLeftTextDetail>{t(tKey('DropOff'))}</CardLeftTextDetail>
          <ShrinkView>
            <XSmallText>
              {item.DropoffTime} {formatDate(item.DropoffDate)}
            </XSmallText>
            <XSmallText style={styles.grayColor}>
              {item.DropAddress
                ? item.DropoffCityState + '/' + item.DropAddress
                : item.DropoffAirport}
            </XSmallText>
          </ShrinkView>
        </RowView>
        <RowView style={CS.my2}>
          <CardLeftTextDetail />
          <ShrinkView>
            {item.IsCarCorporateFareAvailable && (
              <TextWithIcon iconName="corporate" iconColor={Color.corporate}>
                Corporate Rate
              </TextWithIcon>
            )}
            <TextWithIcon iconName="transmission" style={CS.capitalize}>
              {t(tKey(item.CarTransmission))}
            </TextWithIcon>
            <TextWithIcon iconName="mileage">{t(tKey('UnlimitedMileage'))}</TextWithIcon>
            <TextWithIcon iconName="circleCancel">{t(tKey('FreeCancelation'))}</TextWithIcon>
          </ShrinkView>
        </RowView>
      </ShrinkView>
      {showBrands && (
        <View>
          {showPerUnitPrice ? (
            <PerUnitPrice
              label={t(tKey('PerDay'))}
              value={formatCurrency(item.MinimumfarePerDay, currency, currencyCulture)}
            />
          ) : (
            <PriceItem
              price={formatCurrency(item.MinimumfarePerDay, currency, currencyCulture)}
              labelUp={t(tKey('PerDay'))}
              isPrimary={true}
              onPress={() => dispatch(selectCar(item))}
              policyReasonCode={item.OutSidePolicyException?.ReasonCode?.toLowerCase()}
            />
          )}
        </View>
      )}
    </View>
  );
};

const CarCard = ({ item, showBrands, showPerUnitPrice, currency, currencyCulture, ...rest }) => {
  useTranslation(i18nNS.resultCards);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (Object.keys(item).length > 0) {
      setData(transformCarDataToCard(item, currency, currencyCulture));
    }
  }, [item]);

  return data ? (
    <Card
      title={data.title}
      subtitle={data.subtitle}
      value={data.value}
      textUpValue={data.textUpValue}
      textDownValue={data.textDownValue}
      icon={data.icon}
      policy={data.policy}
      imageDownIcon={data.imageDownIcon}
      uppercaseSubtitle
      detail={
        <CarDetails
          item={item}
          showBrands={showBrands}
          currency={currency}
          currencyCulture={currencyCulture}
          showPerUnitPrice={showPerUnitPrice}
        />
      }
      {...rest}
    >
      <View style={styles.accesories}>
        {item.IsCarCorporateFareAvailable && <Icon style={styles.iconAlone} name="corporate" />}
        <AccesoryItem iconName="person" number={item.Passenger} />
        <AccesoryItem iconName="bag" number={item.Luggage} />
        {item.Capacity && <AccesoryItem iconName="door" number={item.Capacity} />}
      </View>
    </Card>
  ) : (
    <></>
  );
};

const styles = StyleSheet.create({
  accesories: {
    ...CommonProperties.row,
    ...CommonProperties.pt1,
  },
  timeText: {
    ...CommonProperties.row,
    ...CommonProperties.wrap,
  },
  grayColor: {
    color: Color.gray,
  },
  iconAlone: {
    fontSize: FontSize.small,
    color: Color.corporate,
  },
  accesoryItemIcon: {
    color: Color.primary,
    fontSize: FontSize.xSmall,
    alignSelf: 'center',
  },
});

export default CarCard;
